/**
 * The Environment card as an editor.
 *
 * Reads the plugin's environment, holds a draft of its override set, and
 * applies the whole draft once as an environment job. The job is polled to
 * its outcome; a success shows the diff, a failure the error tail with the
 * draft kept. Nothing is saved on the server until a job succeeds.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import {
  applyPluginEnvironment,
  getEnvironmentJob,
  getPluginEnvironment,
} from '@optum/api-client/endpoints/environment';
import type {
  ActiveJobOut,
  EnvironmentJobOut,
  OverrideEntry,
  PluginEnvironmentOut,
} from '@optum/api-client/types';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';
import { useToast } from '../../ui/useToast.js';
import { relativeTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { Badge, Inline, Input, Stack, Typography } from '../../ui/finixt.js';
import { ApplyEnvironmentDialog } from './ApplyEnvironmentDialog.js';
import {
  addRequire,
  buildRows,
  dropEntry,
  isDirty,
  removePackage,
  summarizeDraft,
  undoChange,
  type EnvironmentRow,
} from './environment-draft.js';
import { OriginBadge } from './OriginBadge.js';

const POLL_MS = 2000;
const TERMINAL = new Set<EnvironmentJobOut['state']>(['succeeded', 'failed']);
const FIELD_STYLE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--text-body-sm)',
  height: 32,
  padding: '5px 9px',
} as const;

interface Props {
  pluginName: string;
  installedVersion: string | null;
}

/** A specifier editor for one row, or the Add package form when `name` is null. */
interface Editing {
  name: string | null;
  specifier: string;
  newName: string;
}

export function EnvironmentCard({ pluginName, installedVersion }: Props) {
  if (installedVersion === null) {
    return (
      <SectionCard title="Environment">
        <SectionCardBody>
          <Typography variant="meta" as="p">
            Not installed
          </Typography>
        </SectionCardBody>
      </SectionCard>
    );
  }
  return <Editor pluginName={pluginName} installedVersion={installedVersion} />;
}

function Editor({ pluginName, installedVersion }: Props & { installedVersion: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [edits, setEdits] = useState<OverrideEntry[] | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [watchedJobId, setWatchedJobId] = useState<string | null>(null);
  const [validation, setValidation] = useState<string[]>([]);

  const environment = useQuery({
    queryKey: qk.pluginEnvironment(pluginName),
    queryFn: () => getPluginEnvironment(pluginName),
  });

  // A job started from another tab, or before this page loaded, is watched too.
  const activeFromServer = environment.data?.active_job ?? null;
  const jobId =
    watchedJobId ?? (activeFromServer?.kind === 'environment' ? activeFromServer.id : null);
  const job = useQuery({
    queryKey: ['environment-job', jobId],
    queryFn: () => getEnvironmentJob(jobId as string),
    enabled: jobId !== null,
    refetchInterval: (q) => {
      const data = q.state.data;
      return data && TERMINAL.has(data.state) ? false : POLL_MS;
    },
  });
  const running = job.data !== undefined && !TERMINAL.has(job.data.state);
  const blockedByInstall = activeFromServer?.kind === 'install';

  // When the watched job ends, every reader of the environment is stale.
  const outcome = job.data && TERMINAL.has(job.data.state) ? job.data : null;
  const [settledJobId, setSettledJobId] = useState<string | null>(null);
  if (outcome && settledJobId !== outcome.id) {
    setSettledJobId(outcome.id);
    setEdits(null);
    void qc.invalidateQueries({ queryKey: qk.pluginEnvironment(pluginName) });
    void qc.invalidateQueries({ queryKey: qk.pluginEnvironmentJobs(pluginName) });
    void qc.invalidateQueries({ queryKey: ['catalog'] });
  }

  const apply = useMutation({
    mutationFn: (args: { entries: OverrideEntry[]; refresh: boolean }) =>
      applyPluginEnvironment(pluginName, args),
    onSuccess: (started) => {
      setConfirmOpen(false);
      setValidation([]);
      setWatchedJobId(started.id);
      void qc.invalidateQueries({ queryKey: qk.environmentJobsActive() });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        const errors = err.details?.['errors'];
        setValidation(Array.isArray(errors) ? errors.map(String) : [err.message]);
        setConfirmOpen(false);
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        const active = err.details?.['active'] as ActiveJobOut | undefined;
        toast.error(
          'Another job is running for this plugin',
          active ? `An ${active.kind} job is ${active.state}; wait for it to finish.` : err.message,
        );
        setConfirmOpen(false);
        return;
      }
      toast.error('Apply failed', err instanceof ApiError ? err.message : String(err));
    },
  });

  if (environment.isPending) {
    return (
      <SectionCard title="Environment">
        <SectionCardBody>
          <LoadingBlock />
        </SectionCardBody>
      </SectionCard>
    );
  }
  if (environment.isError) {
    return (
      <SectionCard title="Environment">
        <SectionCardBody>
          <ErrorBlock
            message={
              environment.error instanceof Error ? environment.error.message : 'Failed to load'
            }
          />
        </SectionCardBody>
      </SectionCard>
    );
  }

  const data: PluginEnvironmentOut = environment.data;
  const saved = data.overrides;
  const draft = edits ?? saved;
  const dirty = isDirty(saved, draft);
  const rows = buildRows({
    pluginName,
    environment: data.environment,
    requires: data.requires_dist,
    saved,
    draft,
  });
  // Overrides always show; the plain public closure sits behind the disclosure.
  const lead = rows.filter((r) => r.optum || r.entry !== null || r.dirty);
  const rest = rows.filter((r) => !lead.includes(r));
  const shown = showAll ? rows : lead;
  const summary = summarizeDraft(saved, draft, data.requires_dist);
  const changeCount =
    summary.added.length + summary.changed.length + summary.removed.length + summary.dropped.length;
  const locked = running || blockedByInstall || apply.isPending;

  function update(next: OverrideEntry[]) {
    setEdits(next);
    setValidation([]);
  }

  function commitEditing() {
    if (!editing) return;
    const name = editing.name ?? editing.newName.trim();
    if (!name) return;
    update(addRequire(draft, name, editing.specifier));
    setEditing(null);
  }

  return (
    <SectionCard
      title="Environment"
      subtitle={headerSubtitle(installedVersion, dirty, changeCount, running)}
      actions={
        <>
          {' '}
          {running && <Badge tone="info">rebuilding</Badge>}{' '}
          {blockedByInstall && <Badge tone="warn">installing</Badge>}{' '}
          {dirty && !running && (
            <Badge tone="warn">{`draft · ${changeCount} change${changeCount === 1 ? '' : 's'}`}</Badge>
          )}{' '}
          <Button
            variant="secondary"
            size="sm"
            icon="add"
            disabled={locked}
            onClick={() => setEditing({ name: null, specifier: '', newName: '' })}
          >
            {' '}
            Add package{' '}
          </Button>{' '}
        </>
      }
    >
      <SectionCardBody>
        <Stack gap={11}>
          {outcome && <Outcome job={outcome} onDismiss={() => setWatchedJobId(null)} />}
          {running && job.data && <Running job={job.data} />}

          {editing && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                commitEditing();
              }}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 7,
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--ok)',
                borderStyle: 'dashed',
                padding: 7,
              }}
            >
              {editing.name === null ? (
                <Input
                  aria-label="Package name"
                  style={{ ...FIELD_STYLE, minWidth: 0, flex: 1 }}
                  placeholder="package name"
                  value={editing.newName}
                  onChange={(e) => setEditing({ ...editing, newName: e.target.value })}
                />
              ) : (
                <code
                  style={{
                    minWidth: 0,
                    flex: '1 1 0%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'var(--text-meta-lg)',
                  }}
                  className="mono"
                >
                  {editing.name}
                </code>
              )}
              <Input
                aria-label="Specifier"
                style={{ ...FIELD_STYLE, width: 224 }}
                placeholder="version or specifier, e.g. 1.1.0 or >=1,<2"
                value={editing.specifier}
                onChange={(e) => setEditing({ ...editing, specifier: e.target.value })}
              />
              <Button type="submit" variant="primary" size="sm">
                {editing.name === null ? 'Add to draft' : 'Set version'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </form>
          )}

          <Stack gap={5} style={{ fontSize: 'var(--text-meta-lg)' }}>
            {shown.map((row) => (
              <Row
                key={row.name}
                row={row}
                locked={locked}
                onVersion={() =>
                  setEditing({
                    name: row.name,
                    specifier: row.entry?.specifier ?? (row.version ? `==${row.version}` : ''),
                    newName: '',
                  })
                }
                onRemove={() => update(removePackage(draft, row.name))}
                onRevert={() => update(dropEntry(draft, row.name))}
                onUndo={() => update(undoChange(draft, saved, row.name))}
              />
            ))}
          </Stack>

          {rest.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll((v) => !v)}
              style={{ alignSelf: 'flex-start' }}
            >
              {showAll ? 'Show overrides and tools only' : `Show all ${rows.length} packages`}
            </Button>
          )}

          {validation.length > 0 && (
            <ul
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--danger-line)',
                background: 'var(--danger-tint)',
                padding: 7,
                fontSize: 'var(--text-meta-lg)',
                color: 'var(--danger)',
              }}
            >
              {validation.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}

          <Inline
            justify="space-between"
            align="center"
            gap={7}
            wrap={false}
            style={{
              borderTop: '1px solid var(--border-hairline)',
              borderColor: 'var(--border-hairline)',
              paddingTop: 11,
            }}
          >
            <Typography variant="meta" as="span">
              {saved.length === 0
                ? 'No overrides saved'
                : `${saved.length} override${saved.length === 1 ? '' : 's'}${
                    data.overrides_updated_at
                      ? ` · applied ${relativeTime(data.overrides_updated_at)} by ${data.overrides_updated_by ?? '—'}`
                      : ''
                  }`}
            </Typography>
            <Inline gap={7} align="center" wrap={false}>
              {dirty ? (
                <>
                  <Button variant="ghost" size="sm" disabled={locked} onClick={() => update(saved)}>
                    Discard
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={locked}
                    onClick={() => setConfirmOpen(true)}
                  >
                    Apply {changeCount} change{changeCount === 1 ? '' : 's'}
                  </Button>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  icon="refresh"
                  disabled={locked}
                  onClick={() => apply.mutate({ entries: saved, refresh: true })}
                >
                  Rebuild
                </Button>
              )}
            </Inline>
          </Inline>
        </Stack>
      </SectionCardBody>

      <ApplyEnvironmentDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        pluginName={pluginName}
        summary={summary}
        pending={apply.isPending}
        onConfirm={() => apply.mutate({ entries: draft, refresh: false })}
      />
    </SectionCard>
  );
}

function headerSubtitle(
  version: string,
  dirty: boolean,
  changeCount: number,
  running: boolean,
): string {
  if (running) return `v${version} · a new environment is being built and verified.`;
  if (dirty)
    return `v${version} · ${changeCount} edit${changeCount === 1 ? '' : 's'} are local until Apply.`;
  return `v${version} · what this plugin resolved and carries. Nothing here is shared with another plugin.`;
}

function Row({
  row,
  locked,
  onVersion,
  onRemove,
  onRevert,
  onUndo,
}: {
  row: EnvironmentRow;
  locked: boolean;
  onVersion: () => void;
  onRemove: () => void;
  onRevert: () => void;
  onUndo: () => void;
}) {
  const removed = row.origin === 'removed';
  // The name keeps the whole first line; version, badge, and actions wrap
  // beneath it when the column is narrow, so a long name is never cut to a
  // letter. Actions show on hover or focus, as the wireframe draws them.
  return (
    <li
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 7,
        rowGap: 5,
        borderRadius: 'var(--radius-control)',
        padding: 5,
        background: row.dirty ? 'var(--ok-tint)' : undefined,
      }}
    >
      <span style={{ minWidth: 0, flexBasis: '100%' }}>
        <code
          title={row.name}
          className="mono"
          style={{
            display: 'block',
            wordBreak: 'break-all',
            ...(removed ? { textDecoration: 'line-through', color: 'var(--text-faint)' } : {}),
          }}
        >
          {row.name}
        </code>
        {row.declared !== null && row.origin === 'changed' && (
          <span style={{ display: 'block', color: 'var(--text-faint)' }} className="mono">
            manifest {row.declared || 'any'}
          </span>
        )}
      </span>
      <span style={{ display: 'flex', minWidth: 0, flex: '1 1 0%', alignItems: 'center', gap: 7 }}>
        <span
          className="mono"
          style={{
            textDecoration: removed ? 'line-through' : undefined,
            color: removed ? 'var(--text-faint)' : 'var(--text-muted)',
          }}
        >
          {row.entry?.action === 'require' && row.entry.specifier && row.dirty
            ? row.entry.specifier
            : (row.version ?? row.entry?.specifier ?? '—')}
        </span>
        <OriginBadge origin={row.origin} />
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, opacity: 0 }}>
        {row.origin !== 'plugin' && !locked && (
          <>
            {row.dirty && (
              <Button variant="ghost" size="sm" onClick={onUndo}>
                Undo
              </Button>
            )}
            {!row.dirty && row.origin === 'declared' && (
              <>
                <Button variant="ghost" size="sm" onClick={onVersion}>
                  Version
                </Button>
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  Remove
                </Button>
              </>
            )}
            {!row.dirty && (row.origin === 'added' || row.origin === 'changed') && (
              <>
                <Button variant="ghost" size="sm" onClick={onVersion}>
                  Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={onRevert}>
                  Revert
                </Button>
              </>
            )}
            {!row.dirty && removed && (
              <Button variant="ghost" size="sm" onClick={onRevert}>
                Revert
              </Button>
            )}
          </>
        )}
      </span>
    </li>
  );
}

function Running({ job }: { job: EnvironmentJobOut }) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-input)',
        border: '1px solid var(--info)',
        background: 'var(--info-tint)',
        padding: 7,
        fontSize: 'var(--text-meta-lg)',
      }}
    >
      <span style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--info)' }}>
        Rebuilding environment
      </span>
      <span style={{ color: 'var(--text-muted)' }}>
        {' '}
        · started {relativeTime(job.started_at)} by {job.requested_by} · editing is locked until it
        ends
      </span>
    </div>
  );
}

function Outcome({ job, onDismiss }: { job: EnvironmentJobOut; onDismiss: () => void }) {
  const seconds =
    job.finished_at !== null
      ? Math.max(0, Math.round((Date.parse(job.finished_at) - Date.parse(job.started_at)) / 1000))
      : null;
  const meta = `${seconds !== null ? `${seconds}s · ` : ''}${job.backend ?? ''} · by ${job.requested_by}`;
  if (job.state === 'failed') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--danger)',
          background: 'var(--danger-tint)',
          padding: 7,
          fontSize: 'var(--text-meta-lg)',
        }}
      >
        <Inline justify="space-between" align="center" gap={7} wrap={false}>
          <span style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--danger)' }}>
            Apply failed · the live environment is unchanged
          </span>
          <Button variant="ghost" size="sm" onClick={onDismiss} aria-label="Dismiss outcome">
            ×
          </Button>
        </Inline>
        <pre style={{ whiteSpace: 'pre-wrap', color: 'var(--text-soft)' }} className="mono">
          {job.error ?? 'unknown error'}
        </pre>
        <span style={{ color: 'var(--text-muted)' }}>{meta}. Your draft is kept.</span>
      </div>
    );
  }
  const diff = job.diff;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        borderRadius: 'var(--radius-input)',
        border: '1px solid var(--ok)',
        background: 'var(--ok-tint)',
        padding: 7,
        fontSize: 'var(--text-meta-lg)',
      }}
    >
      <Inline justify="space-between" align="center" gap={7} wrap={false}>
        <span style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--ok)' }}>
          Applied · {meta}
        </span>
        <Button variant="ghost" size="sm" onClick={onDismiss} aria-label="Dismiss outcome">
          ×
        </Button>
      </Inline>
      {diff && diff.added.length + diff.removed.length + diff.changed.length === 0 && (
        <span style={{ color: 'var(--text-muted)' }}>
          Nothing moved; the environment is as it was.
        </span>
      )}
      {diff?.added.map((e) => (
        <span key={`a-${e.name}`} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <OriginBadge origin="added" />
          <code className="mono">
            {e.name} {e.version}
          </code>
        </span>
      ))}
      {diff?.changed.map((e) => (
        <span key={`c-${e.name}`} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <OriginBadge origin="changed" />
          <code className="mono">
            {e.name} {e.from} → {e.to}
          </code>
        </span>
      ))}
      {diff?.removed.map((e) => (
        <span key={`r-${e.name}`} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <OriginBadge origin="removed" />
          <code className="mono">
            {e.name} {e.version}
          </code>
        </span>
      ))}
    </div>
  );
}
